import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const ConditionsGeneralesPage = () => {
  useEffect(() => {
    document.title = "Conditions Générales de Vente | Cocoonut Digital Strasbourg";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Conditions Générales de Vente | Cocoonut Digital Strasbourg</title>
        <meta name="description" content="Conditions générales de vente de Cocoonut Digital, studio créatif spécialisé dans la gestion des réseaux sociaux pour restaurants et lieux de vie à Strasbourg." />
        <link rel="canonical" href={`${window.location.origin}/conditions-generales`} />
      </Helmet>
      {/* Header */}
      <div className="bg-[#1a3a52] py-8">
        <div className="container mx-auto px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={20} />
            <span>Retour à l'accueil</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-brand-display text-white">Conditions Générales de Vente</h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <p className="text-sm text-[#717171] mb-8">Cocoonut Concept Creations</p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">1. Identification</h2>
            <div className="text-[#151515]/80 space-y-1">
              <p><strong>GEOFFROY RHAMELLENE</strong></p>
              <p>Nom commercial : Cocoonut Concept Creations</p>
              <p>Entreprise individuelle – Micro-entreprise</p>
              <p>SIREN : 989 162 847</p>
              <p>SIRET : 989 162 847 00010</p>
              <p>Adresse : 8 rue du Commandant L'Herminier, 67640 Fegersheim, France</p>
              <p>Email : rhamgffry@cocoonutconcept.com</p>
              <p className="mt-2">TVA intracommunautaire : FR48989162847</p>
              <p>Code APE : 70.21Z – Conseil en relations publiques et communication</p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">2. Objet</h2>
            <p className="text-[#151515]/80 mb-4">
              Les présentes CGV régissent les prestations de services proposées par Cocoonut Concept Creations, incluant :
            </p>
            <ul className="list-disc list-inside text-[#151515]/80 space-y-2 ml-4">
              <li>Conception de contenu digital</li>
              <li>Gestion et animation de réseaux sociaux</li>
              <li>Stratégie éditoriale</li>
              <li>Accompagnement en communication digitale</li>
              <li>Formation professionnelle pour adultes</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">3. Clientèle</h2>
            <p className="text-[#151515]/80">
              Les prestations sont destinées principalement aux professionnels (B2B).
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">4. Prestations</h2>
            <p className="text-[#151515]/80 mb-4">
              Les prestations font l'objet d'un devis ou contrat précisant :
            </p>
            <ul className="list-disc list-inside text-[#151515]/80 space-y-2 ml-4">
              <li>La nature des services</li>
              <li>La durée</li>
              <li>Les modalités de paiement</li>
            </ul>
            <p className="text-[#151515]/80 mt-4">
              Toute commande implique l'acceptation des présentes CGV.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">5. Tarifs</h2>
            <div className="bg-[#f5e6d3]/30 border-l-4 border-[#d4a574] p-4 rounded-r-lg mb-4">
              <p className="text-[#1a3a52] font-medium">
                Les accompagnements mensuels débutent à partir de 500 € selon le niveau de stratégie et de production de contenu.
              </p>
            </div>
            <p className="text-[#151515]/80">Les tarifs sont exprimés en euros.</p>
            <p className="text-[#151515]/80 mt-2">TVA non applicable, article 293 B du CGI (régime micro-entreprise).</p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">6. Modalités de paiement</h2>
            <p className="text-[#151515]/80 mb-4">Les paiements peuvent être effectués par :</p>
            <ul className="list-disc list-inside text-[#151515]/80 space-y-2 ml-4">
              <li>Virement bancaire</li>
              <li>Prélèvement SEPA</li>
              <li>Autres moyens acceptés par l'entreprise</li>
            </ul>
            <p className="text-[#151515]/80 mt-4">
              Les prestations mensuelles sont payables à l'avance sauf accord contraire.
            </p>
            <p className="text-[#151515]/80 mt-2">
              En cas de paiement par prélèvement SEPA, le client autorise expressément le débit selon les conditions définies dans le contrat.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">7. Retard de paiement</h2>
            <p className="text-[#151515]/80">
              Tout retard peut entraîner la suspension des prestations.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">8. Responsabilité</h2>
            <p className="text-[#151515]/80">
              L'entreprise est tenue à une obligation de moyens. Aucun résultat chiffré précis ne peut être garanti.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">9. Propriété intellectuelle</h2>
            <p className="text-[#151515]/80">
              Les créations demeurent la propriété de Cocoonut Concept Creations jusqu'au paiement intégral.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-[#1a3a52] mb-4">10. Droit applicable</h2>
            <p className="text-[#151515]/80">
              Les présentes CGV sont soumises au droit français.
            </p>
          </section>

        </motion.div>
      </div>
    </div>
  );
};

export default ConditionsGeneralesPage;
